import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const CONSUMER_PAINS = [
  "Too much financial information",
  "Difficulty comparing products",
  "Limited financial education",
  "Poor access to trusted advice",
  "Choosing the wrong products",
];

const INSTITUTION_PAINS = [
  "High customer acquisition costs",
  "Fraud and identity risks",
  "Poor lead quality",
  "Slow verification processes",
  "Increasing loan defaults",
  "Expensive debt recovery",
];

const DELIVERS = [
  "Financial Education",
  "Product Comparison",
  "AI Recommendations",
  "Digital Trust Verification",
  "Qualified Lead Generation",
  "Automated Debt Recovery",
  "Better Customer Experience",
  "Sustainable Growth",
];

function Why() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="why"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="why-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(80%_50%_at_100%_0%,rgba(12,31,27,0.08),transparent_55%),radial-gradient(60%_40%_at_0%_100%,rgba(229,184,11,0.1),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.07) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Why SimFinance exists
          </p>
          <h2
            id="why-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Solving real financial
            <span className="mt-1 block">challenges</span>
          </h2>
        </motion.header>

        <motion.div
          className="mt-14 grid items-stretch gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,0.92fr)_minmax(0,1.16fr)]"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: reduce ? 0 : 0.1 },
            },
          }}
        >
          <PainColumn
            index="01"
            kicker="The friction"
            title="Consumers face"
            items={CONSUMER_PAINS}
            variants={fade}
          />
          <PainColumn
            index="02"
            kicker="The friction"
            title="Financial institutions face"
            items={INSTITUTION_PAINS}
            variants={fade}
          />
          <DeliverColumn items={DELIVERS} variants={fade} reduce={reduce} />
        </motion.div>
      </div>
    </section>
  );
}

function PainColumn({ index, kicker, title, items, variants }) {
  return (
    <motion.article className="flex flex-col bg-white px-6 py-8 sm:px-8 sm:py-10" variants={variants}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
          {kicker}
        </p>
        <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-navy/25">
          {index}
        </span>
      </div>
      <h3 className="mt-5 text-2xl font-semibold tracking-tight text-royal">{title}</h3>
      <p className="mt-2 font-mono text-[11px] tracking-[0.16em] text-navy/35">
        0{items.length} unresolved
      </p>

      <ul className="mt-8 flex-1">
        {items.map((item, i) => (
          <li
            key={item}
            className="flex items-start gap-3.5 border-t border-navy/10 py-3.5 first:border-t-0 first:pt-0"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-navy/15 text-navy/35">
              <MinusIcon />
            </span>
            <span className="text-[15px] leading-snug text-navy/70">
              <span className="mr-2 font-mono text-[10px] tracking-[0.14em] text-navy/30">
                0{i + 1}
              </span>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function DeliverColumn({ items, variants, reduce }) {
  return (
    <motion.article
      className="relative flex flex-col overflow-hidden bg-navy px-6 py-8 text-white sm:px-8 sm:py-10"
      variants={variants}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
      <div className="pointer-events-none absolute -right-16 top-10 h-48 w-48 rounded-full bg-royal/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 left-6 h-40 w-40 rounded-full bg-gold/15 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />

      <div className="relative flex items-center justify-between gap-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold/90">
          The resolution
        </p>
        <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-white/35">
          03
        </span>
      </div>
      <h3 className="relative mt-5 text-2xl font-semibold tracking-tight text-white">
        SimFinance delivers
      </h3>
      <p className="relative mt-2 font-mono text-[11px] tracking-[0.16em] text-white/40">
        0{items.length} outcomes
      </p>

      <ul className="relative mt-8 flex-1">
        {items.map((item, i) => (
          <motion.li
            key={item}
            className="flex items-start gap-3.5 border-t border-white/10 py-3 first:border-t-0 first:pt-0"
            initial={reduce ? false : { opacity: 0, x: 10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={
              reduce ? { duration: 0 } : { duration: 0.35, delay: 0.04 * i, ease: EASE }
            }
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold text-navy">
              <CheckIcon />
            </span>
            <span className="text-[15px] font-medium leading-snug text-white">
              {item}
            </span>
          </motion.li>
        ))}
      </ul>
    </motion.article>
  );
}

function MinusIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-2.5 w-2.5">
      <path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3 w-3">
      <path
        d="M2.4 6.2L4.8 8.6L9.6 3.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Why;
